<template>
  <v-container grid-list-xl>
    <v-layout wrap>
      <!-- HEADER -->
      <v-flex xs12>
        <h1 class="headline">Tool Pickup or Demobilization</h1>
      </v-flex>

      <!-- Searh tools on site -->
      <v-flex xs12 sm6>
        <p class="subheading mb-4">
          Select the tools you want picked up from the list of the tools on
          site.
        </p>
        <v-select
          :items="pickupOptions"
          v-model="pickupType"
          label="Pickup Type"
          prepend-icon="build"
        ></v-select>

        <job-selector @jobSiteChange="jobSite = $event" :whiteBg="true" />
        <v-btn
          class="mb-2 mr-4"
          @click="getToolsOnSite"
          color="primary"
          :disabled="!jobSite"
        >
          <v-icon left>search</v-icon>Find tools on site
        </v-btn>
      </v-flex>

      <!-- Tool list -->
      <v-flex xs12 v-if="toolsOnSite.length">
        <div class="pb-4 title">
          <span class="font-weight-regular d-inline-block pr-3"
            >Tools on site value:</span
          >
          <strong style="border-bottom: solid 3px green"
            >${{ toolsOnSiteValue }}</strong
          >
        </div>
        <p>
          <v-alert type="info">
            <strong>If this list is not correct</strong> please submit your
            current inventory by email to the yard so they can make the
            adjustments.
          </v-alert>
        </p>
        <v-data-table
          :headers="headers"
          :items="toolsOnSite"
          :items-per-page="500"
          item-key="id"
          disable-sort
          hide-default-footer
          :loading="loading"
          loading-text="loading tools on site..."
        >
          <template v-slot:item.image="{ item }">
            <div class="py-0">
              <v-img :src="item.image" width="50" height="50" contain />
            </div>
          </template>
          <template v-slot:item.title="{ item }">
            <div>
              {{ item.title }}
            </div>
            <div>
              <span class="caption green--text text--darken-4"
                >Value: ${{ item.unitPrice && item.unitPrice.toFixed(2) }}</span
              >
            </div>
          </template>
          <template v-slot:item.pickupQty="{ item }">
            <div class="pt-4">
              <v-icon @click="item.pickupQty--" :disabled="item.pickupQty <= 0"
                >remove_circle_outline</v-icon
              >
              <v-text-field
                class="d-inline-block ml-2 centered-input qty"
                name="qty"
                v-model.number="item.pickupQty"
                style="width:60px"
                dense
                type="number"
                :rules="[
                  (v) =>
                    (v <= item.totalQty && v >= 0 && !isNaN(v)) ||
                    'Invalid Qty',
                ]"
              ></v-text-field>
              <v-icon
                @click="item.pickupQty++"
                class="ml-2"
                :disabled="item.pickupQty >= item.totalQty"
                >add_circle_outline</v-icon
              >
              <br />
            </div>
          </template>
          <template v-slot:item.description="{ item }">
            <v-btn
              color="primary"
              text
              outlined
              small
              @click="item.pickupQty = item.totalQty"
              >pickup all</v-btn
            >
          </template>
        </v-data-table>
      </v-flex>
      <v-flex xs12 v-if="loading">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mr-4"
        ></v-progress-circular>
        <span class="caption">Getting tools on site...</span>
      </v-flex>
      <v-flex xs12 v-else>
        <div class="grey--text">{{ noToolsMessage }}</div>
      </v-flex>

      <v-flex xs12 v-if="toolsOnSite.length" style="margin-bottom:80px">
        <p>
          You are requesting a
          <strong>{{ pickupType }}</strong> for site
          <strong v-if="jobSite">{{ jobSite.address }}</strong
          >.
          <br />
          <span class="caption"
            >The yard will be notified and a pickup will be scheduled.</span
          >
        </p>
        <p>
          <v-textarea
            outlined
            label="Notes for yard"
            v-model.trim="notes"
          ></v-textarea>
        </p>
        <p class="mb-4">
          <v-container fluid class="ma-0 pa-0">
            <v-row>
              <v-col cols="12" xs="12" sm="4">
                <v-select
                  :items="pickupDays"
                  v-model="pickupDay"
                  :label="
                    isDemob ? 'Pickup Day (required)' : 'Pickup Day (optional)'
                  "
                  item-value="displayDate"
                  item-text="displayDate"
                  return-object
                ></v-select>
              </v-col>
              <v-col cols="12" xs="12" sm="4">
                <v-select
                  :items="[
                    '6am',
                    '7am',
                    '8am',
                    '9am',
                    '10am',
                    '11am',
                    '12pm',
                    '1pm',
                    '2pm',
                    '3pm',
                    '4pm',
                    '5pm',
                    '6pm',
                    '7pm',
                    '8pm',
                    '9pm',
                    '10pm',
                  ]"
                  v-model="pickupTime"
                  label="Pickup Time (optional)"
                  item-value="displayDate"
                  item-text="displayDate"
                  return-object
                ></v-select>
              </v-col>
              <v-col cols="12" xs="12">
                <v-alert type="error" v-if="pickupType === 'Demobilization'">
                  Do not choose <strong>DEMOBILIZATION</strong> unless your work
                  at this site is finished and you will be
                  <strong>off the site</strong> as of the pickup day you select.
                  You will be asked to sign an attestation confirming this.
                  <br />
                  If anyone is coming back after that day, choose
                  <strong>Tool Pickup</strong> instead.
                </v-alert>
              </v-col>
              <v-col cols="12" xs="12" v-if="isDemob">
                <v-card outlined>
                  <v-card-text>
                    <h4 class="subtitle-1 font-weight-bold mb-3">
                      Demobilization Attestation
                      <span class="caption red--text">(REQUIRED)</span>
                    </h4>
                    <v-alert
                      dense
                      text
                      type="warning"
                      v-if="!pickupDay || !pickupDay.pickupDate"
                    >
                      Select a pickup day above before signing.
                    </v-alert>
                    <template v-else>
                      <p class="body-2">{{ attestationText }}</p>
                      <div v-if="!signatureData">
                        <div class="signature-container mb-3">
                          <VueSignature
                            ref="signature"
                            :w="'400px'"
                            :h="'200px'"
                            :sigOption="signatureOptions"
                          ></VueSignature>
                          <div class="signature-instructions font-weight-thin">
                            Sign here
                          </div>
                        </div>
                        <v-btn
                          small
                          color="secondary"
                          class="mr-2"
                          @click="clearSignature"
                          >Clear</v-btn
                        >
                        <v-btn small color="primary" @click="saveSignature"
                          >Save Signature</v-btn
                        >
                      </div>
                      <div v-else>
                        <h4 class="mb-2">Signature Confirmed &#10003;</h4>
                        <img
                          :src="signatureData"
                          style="max-width: 300px; border: 1px solid #ccc;"
                        />
                        <div class="caption mt-1 mb-2">
                          Signed by: {{ user.firstName }} {{ user.lastName }}
                        </div>
                        <v-btn small color="secondary" @click="clearSignature"
                          >Update Signature</v-btn
                        >
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>

          <v-dialog v-model="dialog" width="500">
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                class="mt-4"
                v-on="on"
                :disabled="!attestationReady"
                >Review</v-btn
              >
            </template>

            <v-card>
              <v-card-title class="headline" primary-title
                >Final Review</v-card-title
              >
              <v-card-text>
                <p v-if="jobSite">{{ jobSite.address }}</p>
                <v-simple-table class="my-4" dense>
                  <template v-slot:default>
                    <tbody>
                      <tr
                        v-for="t in toolsOnSite.filter((t) => t.pickupQty > 0)"
                        :key="t.id"
                      >
                        <td class="pl-0">{{ t.title }}</td>
                        <td>{{ t.pickupQty }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-if="notes">
                  Note for the yard:
                  <br />
                  <strong>{{ notes }}</strong>
                </div>
                <div v-if="isDemob" class="mt-4">
                  <div class="caption grey--text">Attestation</div>
                  <p class="body-2 mb-2">{{ attestationText }}</p>
                  <img
                    v-if="signatureData"
                    :src="signatureData"
                    style="max-width: 220px; border: 1px solid #ccc;"
                  />
                </div>
              </v-card-text>
              <v-card-actions class="pa-2">
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="dialog = false" class="px-4"
                  >Cancel</v-btn
                >
                <v-btn
                  color="primary"
                  @click="placePickupRequest"
                  :disabled="placingOrder"
                  class="px-4"
                  >Submit</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </p>
      </v-flex>
      <v-snackbar v-model="snack" :timeout="5000" :color="snackColor">
        {{ snackText }}
        <v-btn text @click="snack = false">Close</v-btn>
      </v-snackbar>
    </v-layout>
  </v-container>
</template>
<script>
import fb from "@/main";
import moment from "moment";
import { mapState } from "vuex";
import utils from "@/helpers/utils";
import JobSelector from "../components/JobSelector";
import VueSignature from "vue-signature";

// The job's entity is a short label everywhere else in the app. A legal
// attestation needs the full registered name, so map it here rather than
// changing what the rest of the UI displays. Names confirmed by David Looney
// on 9/30. Keep in sync with the Entity dropdown in NewJob.vue and Job.vue.
const ENTITY_LEGAL_NAMES = {
  Volk: "Volk Industrial Services",
  "Alba Services": "ALBA Services",
};

export default {
  components: { JobSelector, VueSignature },
  data() {
    return {
      loading: false,
      placingOrder: false,
      dialog: false,
      jobPickups: [],
      jobSite: null,
      labels: [],
      value: [],
      toolsOnSite: [],
      pickupType: null,
      notes: "",
      pickupOptions: ["Tool Pickup", "Demobilization"],
      headers: [
        {
          text: "",
          align: "left",
          sortable: false,
          value: "image",
          width: 50,
        },
        { text: "", value: "title" },
        { text: "On Site", value: "totalQty", align: "center", width: "50" },
        {
          text: "Pickup Qty",
          value: "pickupQty",
          align: "center",
          width: "160",
        },
        { text: "", value: "description", align: "right", width: "145 " },
      ],
      noToolsMessage: "",
      pickupDay: {},
      pickupDays: [],
      pickupTime: "",
      pickupTimes: [],
      snack: false,
      snackColor: "",
      snackText: "",
      toolsOnSiteValue: "",
      signatureData: null,
      signatureOptions: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)",
      },
    };
  },
  async created() {
    for (let daysBack = 14; daysBack > -1; daysBack--) {
      let day = moment().add(daysBack, "d");
      this.pickupDays.push({
        displayDate: day.format("MMM Do (dddd)"),
        pickupDate: day.toDate(),
      });
    }
    this.pickupDays.reverse();
    if (this.user.role == "Yard Manager") {
      this.pickupOptions.push("First Time Adjustment");
    }
  },
  computed: {
    ...mapState(["user"]),
    isDemob() {
      return this.pickupType === "Demobilization";
    },
    entityLegalName() {
      const entity = this.jobSite && this.jobSite.entity;
      // Entity is not enforced on the job form, so fall back rather than
      // block a foreman in the field. Volk is the safer guess: every job
      // created in 2026 is Volk, and the jobs sitting without an entity are
      // TR trucking records rather than real job sites.
      return ENTITY_LEGAL_NAMES[entity] || ENTITY_LEGAL_NAMES.Volk;
    },
    attestationText() {
      if (!this.isDemob || !this.pickupDay || !this.pickupDay.pickupDate) {
        return "";
      }
      const address = this.jobSite ? this.jobSite.address : "this job site";
      const date = moment(this.pickupDay.pickupDate).format("MMMM Do, YYYY");
      // Counsel's exact wording (D. Friedrich, 8/31). He asked that "off site"
      // be defined exactly as written, so do not soften or reword this.
      return `I hereby attest that, as of ${date}, ${this.entityLegalName} has fully completed its contracted Scope of Work at ${address} and has demobilized and removed its personnel, equipment, and materials from the site, with no remaining contractual work per our Scope of Work to perform and no further presence at the site as of that date.`;
    },
    attestationReady() {
      if (!this.isDemob) {
        return true;
      }
      return !!(
        this.pickupDay &&
        this.pickupDay.pickupDate &&
        this.signatureData
      );
    },
  },
  watch: {
    // The attestation names a specific site and date, so changing either one
    // invalidates a signature that was given against the previous wording.
    pickupType: "clearSignature",
    pickupDay: "clearSignature",
    jobSite: "clearSignature",
  },
  methods: {
    saveSignature() {
      if (!this.$refs.signature) {
        return;
      }
      if (this.$refs.signature.isEmpty()) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Please provide a signature first.";
        return;
      }
      this.signatureData = this.$refs.signature.save();
    },
    clearSignature() {
      if (this.$refs.signature) {
        this.$refs.signature.clear();
      }
      this.signatureData = null;
    },
    async getToolsOnSite() {
      this.noToolsMessage = "";
      this.loading = true;
      try {
        const res = await utils.getToolsOnSite(this.jobSite);
        if (res.msg) {
          this.loading = false;
          this.noToolsMessage = res.msg;
        } else {
          res.toolsOnSite = res.toolsOnSite.map((tos) => {
            return {
              pickupQty: 0,
              ...tos,
            };
          });
          this.loading = false;
          this.toolsOnSite = res.toolsOnSite;
          this.toolsOnSiteValue = res.toolsOnSiteValue;
        }
      } catch (error) {
        alert("Error getting tools on site.");
        this.loading = false;
        console.log(error.message);
      }
    },
    async placePickupRequest() {
      this.placingOrder = true;
      try {
        const pickupTools = this.toolsOnSite.filter((t) => t.pickupQty > 0);
        if (!pickupTools.length) {
          alert("Please select at least one tool to return.");
          return;
        }
        if (this.isDemob && !this.attestationReady) {
          alert("Please select a pickup day and sign the attestation.");
          this.placingOrder = false;
          return;
        }
        await fb.db.runTransaction(async (transaction) => {
          // Get the metadata document and increment the count.
          const metaRef = fb.db.doc("metadata/toolPickups");
          const metaData = (await transaction.get(metaRef)).data() || {};

          const number = metaData.count + 1;

          transaction.update(metaRef, {
            count: number,
          });

          // Update the order document
          const requestRef = fb.db
            .collection("toolPickups")
            .doc(number.toString());

          const request = {
            tools: pickupTools,
            pickupType: this.pickupType || "Tool Pickup",
            orderedBy: this.user,
            jobSite: this.jobSite,
            notes: this.notes,
            orderDate: fb.timestamp,
            orderNumber: number.toString(),
            status: "open",
            toolIds: pickupTools.map((ci) => ci.id),
            pickupDay: this.pickupDay,
            pickupTime: this.pickupTime,
          };

          // Store the rendered wording and the date that was signed against.
          // The yard can still move pickupDay later, but the attestation keeps
          // what the requester actually put their name to.
          if (this.isDemob) {
            request.attestation = {
              text: this.attestationText,
              attestedDate: this.pickupDay.pickupDate,
              signatureData: this.signatureData,
              signedAt: fb.timestamp,
              signedBy: {
                uid: this.user.uid,
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                email: this.user.email,
              },
            };
          }

          transaction.set(requestRef, request);
        });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Successfully Submitted pickup request.";
        this.toolsOnSite = [];
        this.notes = "";
        this.jobSite = null;
        this.pickupType = null;
        this.signatureData = null;
        this.placingOrder = false;
        this.dialog = false;
      } catch (error) {
        console.log(error);
        this.placingOrder = false;
        this.dialog = false;
        alert("Error submitting pickup request: ", error);
      }
    },
  },
};
</script>

<style scoped>
.v-sheet--offset {
  top: -24px;
  position: relative;
}
.signature-container {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
  display: inline-block;
}
.signature-instructions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
  z-index: 1;
}
</style>
